// Hand-written SDK for the custom auth mutations (login / requestPasswordReset /
// register). These live in UserMutations.Custom.cs on the API and are NOT part of
// the generated CRUD SDK surface. Mirrors the other SDKs' constructor(private api)
// pattern and returns the raw {success,error,message,data} envelope for the caller
// (AuthService) to unwrap.
import { Injectable } from '@angular/core';
import { GraphqlApiService } from '../../api/graphql-api.service';

export interface AuthUser {
  id: number;
  email: string | null;
  name: string | null;
}

export interface LoginResult {
  accessToken: string;
  accessTokenExpiresAt: string;
  persona: string; // "admin" | "vendor" | "customer"
  user: AuthUser | null;
  roles: string[];
  vendorId: number | null;
  customerId: number | null;
}

export interface AuthEnvelope<T> {
  success: boolean;
  error?: string | null;
  message?: string | null;
  data?: T | null;
}

export interface RegisterSdkInput {
  name: string;
  email: string;
  password: string;
  accountType: 'vendor' | 'customer';
  businessName?: string | null;
  serviceCategoryId?: number | null;
  phone?: string | null;
  // Vendor onboarding fields (persisted on Vendor + captured as pending verifications).
  ssmNo?: string | null;
  icNo?: string | null;
  bankId?: number | null;
  bankAccountName?: string | null;
  bankAccountNo?: string | null;
  // Verification documents (base64 data-URLs) → VendorVerification.DocumentUrl per check.
  ssmDocUrl?: string | null;
  icDocUrl?: string | null;
  bankDocUrl?: string | null;
}

const LOGIN_RESULT_FIELDS = `
  accessToken accessTokenExpiresAt persona roles vendorId customerId
  user { id email name }
`;

@Injectable({ providedIn: 'root' })
export class AuthSdk {
  constructor(private api: GraphqlApiService) {}

  async login(email: string, password: string, accountType?: string | null): Promise<AuthEnvelope<LoginResult>> {
    // accountType is optional (login has no Vendor/Customer tab): omitted → the API
    // resolves the persona from roles. Kept nullable for backward compatibility.
    const q = `mutation Login($email:String!,$password:String!,$accountType:String){
      login(email:$email,password:$password,accountType:$accountType){
        success error message
        data { ${LOGIN_RESULT_FIELDS} }
      }
    }`;
    const r = await this.api.request<{ login: AuthEnvelope<LoginResult> }>(q, { email, password, accountType: accountType ?? null });
    return r.login;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<AuthEnvelope<unknown>> {
    const q = `mutation ChangePassword($currentPassword:String!,$newPassword:String!){
      changePassword(currentPassword:$currentPassword,newPassword:$newPassword){ success error message }
    }`;
    const r = await this.api.request<{ changePassword: AuthEnvelope<unknown> }>(q, { currentPassword, newPassword });
    return r.changePassword;
  }

  async requestPasswordReset(email: string): Promise<AuthEnvelope<unknown>> {
    const q = `mutation RequestReset($email:String!){
      requestPasswordReset(email:$email){ success error message }
    }`;
    const r = await this.api.request<{ requestPasswordReset: AuthEnvelope<unknown> }>(q, { email });
    return r.requestPasswordReset;
  }

  async register(input: RegisterSdkInput): Promise<AuthEnvelope<LoginResult>> {
    const q = `mutation Register(
      $name:String!,$email:String!,$password:String!,$accountType:String!,
      $businessName:String,$serviceCategoryId:Long,$phone:String,
      $ssmNo:String,$icNo:String,$bankId:Long,$bankAccountName:String,$bankAccountNo:String,
      $ssmDocUrl:String,$icDocUrl:String,$bankDocUrl:String
    ){
      register(
        name:$name,email:$email,password:$password,accountType:$accountType,
        businessName:$businessName,serviceCategoryId:$serviceCategoryId,phone:$phone,
        ssmNo:$ssmNo,icNo:$icNo,bankId:$bankId,bankAccountName:$bankAccountName,bankAccountNo:$bankAccountNo,
        ssmDocUrl:$ssmDocUrl,icDocUrl:$icDocUrl,bankDocUrl:$bankDocUrl
      ){
        success error message
        data { ${LOGIN_RESULT_FIELDS} }
      }
    }`;
    const r = await this.api.request<{ register: AuthEnvelope<LoginResult> }>(q, {
      name: input.name,
      email: input.email,
      password: input.password,
      accountType: input.accountType,
      businessName: input.businessName ?? null,
      serviceCategoryId: input.serviceCategoryId ?? null,
      phone: input.phone ?? null,
      ssmNo: input.ssmNo ?? null,
      icNo: input.icNo ?? null,
      bankId: input.bankId ?? null,
      bankAccountName: input.bankAccountName ?? null,
      bankAccountNo: input.bankAccountNo ?? null,
      ssmDocUrl: input.ssmDocUrl ?? null,
      icDocUrl: input.icDocUrl ?? null,
      bankDocUrl: input.bankDocUrl ?? null,
    });
    return r.register;
  }
}
